import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { aboutData, aboutStats, aboutMission } from '../data';
import { Breadcrumb } from '../components/Breadcrumb';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div>
      <SEO
        title="About Us"
        description="Learn about Aashi Constructions - 25+ years of expertise in Bangalore construction. Our story, mission, and commitment to quality."
        keywords="about aashi constructions, construction company bangalore history, building contractors about us"
        url="/about"
      />
      <Breadcrumb items={[{ label: 'About Us' }]} />
      
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src={aboutData.founderImage}
          alt="About us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Our Story
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            A legacy of quality and trust in construction
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-brand-100 text-brand-800 rounded-full text-sm font-semibold mb-6">
                Our Story
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                {aboutData.title}
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {aboutData.story}
              </p>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-200 to-brand-100 rounded-3xl transform -rotate-3" />
                <img
                  src={aboutData.founderImage}
                  alt="Our Story"
                  loading="lazy"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            {aboutData.values.map((value, index) => (
              <div
                key={index}
                className="text-center p-4 sm:p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
              >
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-brand-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block px-4 py-2 bg-brand-100 text-brand-800 rounded-full text-sm font-semibold mb-6">
                Meet Our Team
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
                {aboutData.founderName}
              </h2>
              <p className="text-lg md:text-xl text-brand-600 font-medium mb-6">
                {aboutData.founderTitle}
              </p>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  With over 10+ years of experience in residential and commercial construction across Bangalore, Aashi Constructions has established itself as a trusted name in the industry. Our unwavering commitment to quality materials and transparent practices has delivered countless satisfied homeowners.
                </p>
                <p>
                  Under our leadership, the company has become synonymous with quality craftsmanship, attention to detail, and complete client satisfaction. Each project is approached as a unique opportunity to build lasting relationships and structures that stand the test of time.
                </p>
                <p>
                  Our philosophy is simple: "A home is not just a structure; it's where memories are made and families grow. Our mission is to build homes that provide comfort, safety, and lasting value while maintaining the highest standards of construction quality."
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-200 to-brand-100 rounded-3xl transform rotate-3" />
                <img
                  src={aboutData.storyImage}
                  alt={aboutData.founderName}
                  loading="lazy"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Our Numbers
            </h2>
            <p className="text-gray-300 text-base md:text-lg">
              A track record of quality and reliability
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-brand-500 mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-brand-100 text-brand-800 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                From Our Founder
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 sm:mb-6">
                A Note from Our Founder
              </h2>
              <div className="space-y-3 sm:space-y-4 text-gray-600 leading-relaxed">
                {aboutData.founderNote?.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-br from-brand-200 to-brand-100 rounded-2xl sm:rounded-3xl transform -rotate-3" />
                <img
                  src={aboutData.noteImage}
                  alt="Our Founder"
                  loading="lazy"
                  className="relative rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
            Our Mission
          </h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            {aboutMission}
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
